import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { fetchWrapper } from '../helpers';
import { API_ENDPOINT, ClinicalRelationships } from '../constants';

import type { IProviderAssignment } from '../utils/types';
import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

const name = 'providerAssignment';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<ProviderAssignmentState>) =>
    createExtraReducers(builder),
});

export interface ProviderAssignmentState {
  list: Array<IProviderAssignment>;
  practicingGuardianAssignments: Array<IProviderAssignment>;
  practicingGuardianAssignmentsLoading: boolean;
  clientId: number | number[] | null;
  providerId: number | number[] | null;
  error: any;
  loading: boolean;
  // Latest getList request — drop stale/aborted results so they cannot clear loading for a newer fetch.
  requestId: string | null;
}

function createInitialState(): ProviderAssignmentState {
  return {
    list: [],
    practicingGuardianAssignments: [],
    practicingGuardianAssignmentsLoading: false,
    clientId: null,
    providerId: null,
    loading: false,
    error: null,
    requestId: null,
  };
}

function createReducers() {
  return {
    clearAssignment,
    clearPracticingGuardianAssignments,
  };
  function clearAssignment(state: ProviderAssignmentState) {
    state.clientId = null;
    state.providerId = null;
    state.list = [];
  }
  function clearPracticingGuardianAssignments(state: ProviderAssignmentState) {
    state.practicingGuardianAssignments = [];
    state.practicingGuardianAssignmentsLoading = false;
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/provider_assignments`;

  return {
    getList: getList(),
    getPracticingGuardianAssignments: getPracticingGuardianAssignments(),
    createProviderAssignment: createProviderAssignment(),
    updateProviderAssignment: updateProviderAssignment(),
    deleteProviderAssignment: deleteProviderAssignment(),
  };

  function getPracticingGuardianAssignments() {
    return createAsyncThunk(
      `${name}/getPracticingGuardianAssignments`,
      async (
        {
          providerId,
          getAccessTokenSilently,
        }: {
          providerId: number;
          getAccessTokenSilently: () => Promise<string>;
        },
        thunkAPI,
      ) => {
        const searchParams = new URLSearchParams({
          active_only: 'true',
          provider_id: String(providerId),
          clinical_relationship: ClinicalRelationships.PracticingGuardian,
        });
        return fetchWrapper.get({
          url: `${baseUrl}/?${searchParams.toString()}`,
          getAccessTokenSilently,
          signal: thunkAPI.signal,
        });
      },
    );
  }

  function getList() {
    return createAsyncThunk(
      `${name}/getList`,
      async ({ getAccessTokenSilently, ...queryParams }: any = {}) => {
        let clientIds = [];
        if (queryParams.client_id) {
          clientIds = Array.isArray(queryParams.client_id)
            ? queryParams.client_id
            : [queryParams.client_id];
        }

        let clinicalRelationships = [];
        if (queryParams.clinical_relationship) {
          clinicalRelationships = Array.isArray(queryParams.clinical_relationship)
            ? queryParams.clinical_relationship
            : [queryParams.clinical_relationship];
        }

        const queryParamsString = Object.keys(queryParams)
          .filter(
            (key) =>
              queryParams[key] !== undefined &&
              key !== 'client_id' &&
              key !== 'clinical_relationship',
          )
          .map((key) => `${key}=${queryParams[key]}`)
          .join('&');

        const clientIdsString =
          clientIds.length > 0 ? clientIds.map((id: number) => `client_id=${id}`).join('&') : '';
        const clinicalRelationshipsString =
          clinicalRelationships.length > 0
            ? clinicalRelationships.map((id: number) => `clinical_relationship=${id}`).join('&')
            : '';
        const queryParamPrefix =
          queryParamsString && (clientIdsString || clinicalRelationshipsString) ? '&' : '';
        const url = `${baseUrl}/?${queryParamsString}${
          clientIdsString ? `${queryParamPrefix}${clientIdsString}` : ''
        }${clinicalRelationshipsString ? `${queryParamPrefix}${clinicalRelationshipsString}` : ''}`;
        const response = await fetchWrapper.get({
          url,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createProviderAssignment() {
    return createAsyncThunk(
      `${name}/createProviderAssignment`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function updateProviderAssignment() {
    return createAsyncThunk(
      `${name}/updateProviderAssignment`,
      async ({ data, providerAssignmentId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          { url: `${baseUrl}/${providerAssignmentId}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function deleteProviderAssignment() {
    return createAsyncThunk(
      `${name}/deleteProviderAssignment`,
      async ({ providerAssignmentId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${providerAssignmentId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<ProviderAssignmentState>) {
  const {
    getList,
    getPracticingGuardianAssignments,
    createProviderAssignment,
    updateProviderAssignment,
    deleteProviderAssignment,
  } = extraActions;
  builder

    //getList() - Fetch provider assignments
    .addCase(
      getList.pending,
      (state: ProviderAssignmentState, action: { meta: { requestId: string } }) => {
        state.requestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      },
    )
    .addCase(
      getList.fulfilled,
      (
        state: ProviderAssignmentState,
        action: PayloadAction<any, string, { requestId: string }>,
      ) => {
        if (action.meta.requestId !== state.requestId) return;

        const payloadArray = action.payload;
        const clientIds = payloadArray.map((item: IProviderAssignment) => item.client.id);
        const providerIds = payloadArray.map((item: IProviderAssignment) => item.provider.id);

        state.clientId =
          clientIds.length > 0 && clientIds.every((id: number) => id === clientIds[0])
            ? clientIds[0]
            : clientIds;
        state.providerId =
          providerIds.length > 0 && providerIds.every((id: number) => id === providerIds[0])
            ? providerIds[0]
            : providerIds;

        state.list = action.payload;
        state.loading = false;
      },
    )
    .addCase(getList.rejected, (state: ProviderAssignmentState, action: any) => {
      if (action.meta.requestId !== state.requestId) return;
      state.loading = false;
      if (action.meta.aborted) return;
      state.error = action.error;
      toast.error(state.error.message);
    })

    .addCase(getPracticingGuardianAssignments.pending, (state: ProviderAssignmentState) => {
      state.practicingGuardianAssignments = [];
      state.practicingGuardianAssignmentsLoading = true;
      state.error = null;
    })
    .addCase(
      getPracticingGuardianAssignments.fulfilled,
      (state: ProviderAssignmentState, action: PayloadAction<IProviderAssignment[]>) => {
        state.practicingGuardianAssignments = action.payload;
        state.practicingGuardianAssignmentsLoading = false;
      },
    )
    .addCase(
      getPracticingGuardianAssignments.rejected,
      (state: ProviderAssignmentState, action: any) => {
        if (action.meta.aborted) return;
        state.practicingGuardianAssignments = [];
        state.practicingGuardianAssignmentsLoading = false;
        state.error = action.error;
        toast.error(action.error.message ?? 'Failed to load clients for questionnaire', {
          toastId: 'questionnaire-client-options-error',
        });
      },
    )

    //createProviderAssignment() - Assign provider to client
    .addCase(createProviderAssignment.pending, (state: ProviderAssignmentState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createProviderAssignment.fulfilled,
      (state: ProviderAssignmentState, action: PayloadAction<IProviderAssignment>) => {
        state.providerId = action.payload.provider.id ?? null;
        state.clientId = action.payload.client.id ?? null;
        state.loading = false;
        toast.success('Provider assigned successfully');
      },
    )
    .addCase(createProviderAssignment.rejected, (state: ProviderAssignmentState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    //updateProviderAssignment() - Update provider assignment
    .addCase(updateProviderAssignment.pending, (state: ProviderAssignmentState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateProviderAssignment.fulfilled,
      (state: ProviderAssignmentState, action: PayloadAction<IProviderAssignment>) => {
        state.providerId = action.payload.provider.id ?? null;
        state.clientId = action.payload.client.id ?? null;
        state.loading = false;
        toast.success('Provider assignment updated successfully');
      },
    )
    .addCase(updateProviderAssignment.rejected, (state: ProviderAssignmentState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    //deleteProviderAssignment() - Delete provider assignment
    .addCase(deleteProviderAssignment.pending, (state: ProviderAssignmentState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      deleteProviderAssignment.fulfilled,
      (state: ProviderAssignmentState, _action: PayloadAction<any>) => {
        state.loading = false;
        toast.success('Provider assignment deleted successfully');
      },
    )
    .addCase(deleteProviderAssignment.rejected, (state: ProviderAssignmentState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    });
}

export const providerAssignmentActions = { ...slice.actions, ...extraActions };
export const providerAssignmentReducer = slice.reducer;
